// src/logger/index.ts
var LOG_LEVELS = {
  trace: 0,
  debug: 1,
  info: 2,
  warn: 3,
  error: 4,
  fatal: 5
};
var ClientLogger = class _ClientLogger {
  endpoint = "";
  minLevel = LOG_LEVELS.info;
  enableConsole = true;
  service = "flote-app";
  batchSize = 10;
  batchTimeout = 5e3;
  logBatch = [];
  batchTimer = null;
  isConfigured = false;
  constructor(options = {}) {
    this.updateConfig(options);
    this.setupEventListeners();
  }
  updateConfig(options = {}) {
    if (options.endpoint) {
      this.endpoint = options.endpoint;
    }
    if (options.level) {
      this.minLevel = LOG_LEVELS[options.level];
    }
    if (options.enableConsole !== void 0) {
      this.enableConsole = options.enableConsole;
    }
    if (options.service) {
      this.service = options.service;
    }
    if (options.batchSize) {
      this.batchSize = options.batchSize;
    }
    if (options.batchTimeout) {
      this.batchTimeout = options.batchTimeout;
    }
    this.isConfigured = true;
  }
  setupEventListeners() {
    if (typeof window !== "undefined") {
      window.addEventListener("beforeunload", () => {
        this.flush();
      });
      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "hidden") {
          this.flush();
        }
      });
    }
  }
  getHostInfo() {
    if (typeof window === "undefined") {
      return { hostname: "server", href: "" };
    }
    return {
      hostname: window.location.hostname,
      href: window.location.href,
      pathname: window.location.pathname,
      userAgent: navigator.userAgent,
      referrer: document.referrer || void 0
    };
  }
  shouldLog(level) {
    return LOG_LEVELS[level] >= this.minLevel;
  }
  consoleLog(level, message, context) {
    if (!this.enableConsole) return;
    const logMethod = level === "trace" || level === "debug" ? "debug" : level === "info" ? "info" : level === "warn" ? "warn" : "error";
    const prefix = `[${this.service}:${level.toUpperCase()}]`;
    if (context && Object.keys(context).length > 0) {
      console[logMethod](prefix, message, context);
    } else {
      console[logMethod](prefix, message);
    }
  }
  addToBatch(entry) {
    this.logBatch.push(entry);
    if (this.logBatch.length >= this.batchSize) {
      this.flush();
    } else if (!this.batchTimer) {
      this.batchTimer = window.setTimeout(() => {
        this.flush();
      }, this.batchTimeout);
    }
  }
  async sendLogs(logs) {
    if (!this.isConfigured || !this.endpoint) {
      return;
    }
    try {
      const promises = logs.map(
        (log) => fetch(this.endpoint, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            level: log.level,
            message: log.message,
            context: {
              ...log.context,
              service: this.service,
              timestamp: log.timestamp
            },
            hostInfo: log.hostInfo
          })
        })
      );
      await Promise.all(promises);
    } catch (error) {
      if (this.enableConsole) {
        console.error("[ClientLogger] Failed to send logs:", error);
      }
    }
  }
  log(level, message, context) {
    if (!this.shouldLog(level)) return;
    this.consoleLog(level, message, context);
    const entry = {
      level,
      message,
      context,
      hostInfo: this.getHostInfo(),
      timestamp: (/* @__PURE__ */ new Date()).toISOString()
    };
    this.addToBatch(entry);
  }
  flush() {
    if (this.logBatch.length === 0) return;
    const logsToSend = [...this.logBatch];
    this.logBatch = [];
    if (this.batchTimer) {
      clearTimeout(this.batchTimer);
      this.batchTimer = null;
    }
    this.sendLogs(logsToSend);
  }
  child(bindings) {
    const childLogger = new _ClientLogger({
      endpoint: this.endpoint,
      level: Object.keys(LOG_LEVELS).find(
        (k) => LOG_LEVELS[k] === this.minLevel
      ) || "info",
      enableConsole: this.enableConsole,
      service: this.service,
      batchSize: this.batchSize,
      batchTimeout: this.batchTimeout
    });
    const originalLog = childLogger.log.bind(childLogger);
    childLogger.log = (level, message, context) => {
      originalLog(level, message, { ...bindings, ...context });
    };
    return childLogger;
  }
  trace(message, context) {
    this.log("trace", message, context);
  }
  debug(message, context) {
    this.log("debug", message, context);
  }
  info(message, context) {
    this.log("info", message, context);
  }
  warn(message, context) {
    this.log("warn", message, context);
  }
  error(message, context) {
    if (message instanceof Error) {
      this.log("error", message.message, {
        error: message.name,
        stack: message.stack,
        ...context
      });
    } else {
      this.log("error", message, context);
    }
  }
  fatal(message, context) {
    if (message instanceof Error) {
      this.log("fatal", message.message, {
        error: message.name,
        stack: message.stack,
        ...context
      });
    } else {
      this.log("fatal", message, context);
    }
  }
};
var logger = new ClientLogger();
function configureLogger(options) {
  logger.updateConfig(options);
}
if (typeof window !== "undefined") {
  window.addEventListener("error", (event) => {
    logger.error("Unhandled error", {
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      message: event.message,
      stack: event.error?.stack
    });
  });
  window.addEventListener("unhandledrejection", (event) => {
    logger.error("Unhandled promise rejection", {
      reason: event.reason?.toString(),
      stack: event.reason?.stack
    });
  });
}

export {
  LOG_LEVELS,
  ClientLogger,
  logger,
  configureLogger
};
